import React, {useState, useEffect, useRef, useCallback} from 'react';
import {Check, ChevronsLeft, ChevronsRight, X} from 'lucide-react';
import {calendarDays, filterBlack, plusBlack} from 'assets/images/images';
import DateFilter from './DateFilter';
import {dateRanges, formatDate} from 'helpers/getDateRange';
import moment from 'moment';
import {toast} from 'react-toastify';
import {DualRangeSlider} from './Sliders/DualRangeSlider';

export const timeOptions = [
  {value: 'today', label: 'Today'},
  {value: 'yesterday', label: 'Yesterday'},
  {value: 'seven_days', label: 'Last 7 days'},
  {value: 'this_month', label: 'This Month'},
  {value: 'last_month', label: 'Last Month'},
  {value: 'last_three_month', label: 'Last 3 Months'},
  {value: 'last_six_month', label: 'Last 6 Months'},
  {value: 'specificDateRange', label: 'Specific Date Range'},
];

const defaultDateFilter = {
  key: 'date',
  label: 'Date',
  type: 'date',
  options: timeOptions,
};

function FilterV2({
  filters = [],
  onFilterChange,
  initialValues = {},
  disableDefaultDateFilter = false,
}) {
  const allFilters = disableDefaultDateFilter
    ? filters
    : [defaultDateFilter, ...filters];
  const [filterValues, setFilterValues] = useState(initialValues);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const scrollContainerRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const prevDateRef = useRef(null);
  const [startDate, setStartDate] = useState(
    initialValues.start_date ? new Date(initialValues.start_date) : null,
  );
  const [endDate, setEndDate] = useState(
    initialValues.end_date ? new Date(initialValues.end_date) : null,
  );
  const hasActiveFilters = Object.keys(filterValues).some(
    key => filterValues[key] !== null && filterValues[key] !== undefined,
  );
  const filterButtonRefs = useRef({});
  const [dropdownPosition, setDropdownPosition] = useState({top: 0, left: 0});
  const [tempRangeValues, setTempRangeValues] = useState({});
  const prevInitialValuesRef = useRef(initialValues);

  useEffect(() => {
    if (
      JSON.stringify(initialValues) !==
      JSON.stringify(prevInitialValuesRef.current)
    ) {
      setFilterValues(currentValues => ({...currentValues, ...initialValues}));
      if (initialValues.start_date && initialValues.end_date) {
        const newStartDate = new Date(initialValues.start_date);
        const newEndDate = new Date(initialValues.end_date);
        if (
          newStartDate.getTime() !== (startDate ? startDate.getTime() : null) ||
          newEndDate.getTime() !== (endDate ? endDate.getTime() : null)
        ) {
          setStartDate(newStartDate);
          setEndDate(newEndDate);
        }
      }
      prevInitialValuesRef.current = initialValues;
    }
  }, [initialValues, startDate, endDate]);

  const checkOverflow = useCallback(() => {
    if (scrollContainerRef.current && containerRef.current) {
      const thresholdWidth = containerRef.current.clientWidth * 0.8;
      const contentWidth = scrollContainerRef.current.scrollWidth;
      setIsOverflowing(contentWidth > thresholdWidth);
    } else {
      setIsOverflowing(false);
    }
  }, []);

  useEffect(() => {
    checkOverflow();
  }, [checkOverflow, filterValues, allFilters.length]);

  useEffect(() => {
    if (
      openDropdown &&
      filterButtonRefs.current[openDropdown] &&
      containerRef.current
    ) {
      const triggerElement = filterButtonRefs.current[openDropdown];
      const containerElement = containerRef.current;

      if (!triggerElement || !containerElement) return;

      const triggerRect = triggerElement.getBoundingClientRect();
      const containerRect = containerElement.getBoundingClientRect();

      let newTop = triggerRect.bottom - containerRect.top;
      let newLeft = triggerRect.left - containerRect.left;

      const dropdownEstimatedHeight = 300;
      const dropdownWidth = 250;

      if (newLeft + dropdownWidth > containerRect.right - containerRect.left) {
        newLeft = triggerRect.right - containerRect.left - dropdownWidth;
      }
      if (newLeft < 0) {
        newLeft = 0;
      }

      if (
        newTop + dropdownEstimatedHeight >
        window.innerHeight - containerRect.top
      ) {
        if (triggerRect.top - containerRect.top - dropdownEstimatedHeight > 0) {
          newTop =
            triggerRect.top - containerRect.top - dropdownEstimatedHeight - 8;
        } else {
          newTop =
            window.innerHeight -
            containerRect.top -
            dropdownEstimatedHeight -
            8;
        }
      }
      if (newTop < 0) {
        newTop = 0;
      }

      setDropdownPosition({
        top: newTop,
        left: newLeft,
      });
    }
  }, [openDropdown]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        const clickedOnAnyFilterButton = Object.values(
          filterButtonRefs.current,
        ).some(ref => ref && ref.contains(event.target));
        if (!clickedOnAnyFilterButton) {
          setOpenDropdown(null);
          setTempRangeValues({});
        }
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const processDateRange = useCallback(dateValue => {
    if (
      dateValue &&
      typeof dateValue === 'string' &&
      dateValue !== 'specificDateRange'
    ) {
      const dateRangeFn = dateRanges[dateValue];
      if (dateRangeFn) {
        return dateRangeFn();
      }
    }
    return null;
  }, []);

  const updateFilterValues = useCallback(
    newValues => {
      if (JSON.stringify(newValues) === JSON.stringify(filterValues)) {
        return;
      }

      if (!disableDefaultDateFilter) {
        const dateValue = newValues.date;
        const prevDateValue = prevDateRef.current;

        if (dateValue !== prevDateValue) {
          prevDateRef.current = dateValue;

          if (
            dateValue &&
            typeof dateValue === 'string' &&
            dateValue !== 'specificDateRange'
          ) {
            const dateRange = processDateRange(dateValue);
            if (dateRange) {
              newValues.dateRange = dateRange;
              setStartDate(null);
              setEndDate(null);
            }
          } else if (!dateValue) {
            delete newValues.dateRange;
            setStartDate(null);
            setEndDate(null);
          }
        }
      }

      setFilterValues(newValues);

      if (onFilterChange) {
        onFilterChange(newValues);
      }
    },
    [onFilterChange, processDateRange, disableDefaultDateFilter, filterValues],
  );

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({left: -200, behavior: 'smooth'});
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({left: 200, behavior: 'smooth'});
    }
  };

  const toggleDropdown = key => {
    if (openDropdown !== key) {
      setTempRangeValues({});
    }
    setOpenDropdown(openDropdown === key ? null : key);
  };

  const handleOptionSelect = (filterKey, option) => {
    const filter = allFilters.find(f => f.key === filterKey);
    if (!filter) return;

    const newFilterValues = {...filterValues};

    if (filter.multiSelect) {
      const currentValues = newFilterValues[filterKey] || [];
      const optionValue = option.value;

      if (currentValues.includes(optionValue)) {
        newFilterValues[filterKey] = currentValues.filter(
          v => v !== optionValue,
        );
      } else {
        newFilterValues[filterKey] = [...currentValues, optionValue];
      }
      updateFilterValues(newFilterValues);
    } else {
      newFilterValues[filterKey] = option.value;
      updateFilterValues(newFilterValues);
      if (!(filterKey === 'date' && option.value === 'specificDateRange')) {
        setOpenDropdown(null);
      }
    }
  };

  const clearFilter = (e, key) => {
    e.stopPropagation();
    const newFilterValues = {...filterValues};
    delete newFilterValues[key];

    if (key === 'date' && !disableDefaultDateFilter) {
      delete newFilterValues.dateRange;
      setStartDate(null);
      setEndDate(null);
    }

    updateFilterValues(newFilterValues);
    if (openDropdown === key) {
      setOpenDropdown(null);
    }
  };

  const clearAllFilters = () => {
    updateFilterValues({});
    setOpenDropdown(null);
    setStartDate(null);
    setEndDate(null);
    setTempRangeValues({});
  };

  const handleCustomDateRangeChange = useCallback(
    (start, end, shouldCloseDropdown = false) => {
      if (disableDefaultDateFilter) return;

      if (start && end) {
        const startMoment = moment(start);
        const endMoment = moment(end);

        if (endMoment.diff(startMoment, 'months', true) > 6) {
          toast.error('Maximum 6 month range allowed');
          return;
        }

        setStartDate(start);
        setEndDate(end);

        updateFilterValues({
          ...filterValues,
          date: 'specificDateRange',
          start_date: formatDate(start),
          end_date: formatDate(end),
          dateRange: {start: formatDate(start), end: formatDate(end)},
        });

        if (shouldCloseDropdown) {
          setOpenDropdown(null);
        }
      }
    },
    [filterValues, updateFilterValues, disableDefaultDateFilter],
  );

  const handleTempRangeChange = (filterKey, type, value) => {
    const filter = allFilters.find(f => f.key === filterKey);
    if (!filter || filter.type !== 'range') return;

    setTempRangeValues(prev => ({
      ...prev,
      [filterKey]: {
        ...prev[filterKey],
        [type]: value !== '' ? parseFloat(value) : null,
      },
    }));
  };

  const handleSliderChange = (filterKey, type, value) => {
    setTempRangeValues(prev => ({
      ...prev,
      [filterKey]: {
        ...prev[filterKey],
        [type]: value,
      },
    }));
  };

  const applyRangeFilter = filterKey => {
    const filter = allFilters.find(f => f.key === filterKey);
    if (!filter || filter.type !== 'range') return;

    const tempValues = tempRangeValues[filterKey] || {};
    const min = tempValues.min !== undefined ? tempValues.min : null;
    const max = tempValues.max !== undefined ? tempValues.max : null;

    if (min !== null && max !== null && min > max) {
      toast.error(`${filter.label} min cannot be greater than max`);
      return;
    }

    const newFilterValues = {
      ...filterValues,
      [filterKey]: min !== null || max !== null ? {min, max} : null,
    };

    updateFilterValues(newFilterValues);
    setOpenDropdown(null);
    setTempRangeValues(prev => ({...prev, [filterKey]: {}}));
  };

  const getFilterDisplayValue = filter => {
    const value = filterValues[filter.key];
    if (!value) return null;

    if (filter.type === 'date' && !disableDefaultDateFilter) {
      if (value === 'specificDateRange') {
        const startDate = filterValues.start_date;
        const endDate = filterValues.end_date;
        if (startDate && endDate) {
          return `${moment(startDate).format('MM/DD/YYYY')} - ${moment(
            endDate,
          ).format('MM/DD/YYYY')}`;
        }
        return 'Select dates';
      } else {
        const option = timeOptions.find(opt => opt.value === value);
        return option?.label || value;
      }
    }

    if (filter.type === 'range') {
      const min = value.min;
      const max = value.max;
      if (min !== null && max !== null) {
        return `${min} - ${max}`;
      } else if (min !== null) {
        return `Min: ${min}`;
      } else if (max !== null) {
        return `Max: ${max}`;
      }
      return null;
    }

    if (filter.multiSelect && Array.isArray(value)) {
      if (value.length === 0) return null;
      if (value.length === 1) {
        const option = filter.options?.find(opt => opt.value === value[0]);
        return option?.label || value[0];
      }
      const firstOption = filter.options?.find(opt => opt.value === value[0]);
      return firstOption?.label || value[0];
    }

    if (typeof value === 'string') {
      const option = filter.options?.find(opt => opt.value === value);
      return option?.label || value;
    }
    return null;
  };

  const getCountBadge = filter => {
    const value = filterValues[filter.key];
    if (filter.multiSelect && Array.isArray(value) && value.length > 1) {
      return value.length - 1;
    }
    return null;
  };

  const currentOpenFilter = allFilters.find(f => f.key === openDropdown);

  return (
    <>
      <div
        ref={containerRef}
        className="mx-[-32px] flex items-center justify-between px-8 border border-transparent border-b-[#E4E4E6] relative"
      >
        <div
          className={`flex items-center gap-2 py-3 md:max-w-[600px] xl:max-w-[1000px] ${
            isOverflowing
              ? 'border-[0.5px] border-transparent border-x-[#E0E4EB]'
              : ''
          } relative`}
        >
          <div className="relative w-full">
            {isOverflowing && (
              <div
                className="absolute left-[-9px] top-1/2 -translate-y-1/2"
                style={{zIndex: 10}}
              >
                <button
                  onClick={scrollLeft}
                  className="bg-blue-500 border border-[#BFD3F3] rounded-full bg-brandBlue p-1 w-[18px] h-[18px] flex items-center justify-center text-white"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
              </div>
            )}

            <div
              ref={scrollContainerRef}
              className="flex items-center gap-2 overflow-x-auto scrollbar-hide flex-nowrap whitespace-nowrap"
              style={{scrollBehavior: 'smooth'}}
            >
              {allFilters.map(filter => (
                <div key={filter.key} className="relative">
                  <button
                    ref={el => (filterButtonRefs.current[filter.key] = el)}
                    onClick={() => toggleDropdown(filter.key)}
                    className="flex items-center h-7 shadow-filter border-[0.5px] border-[#BFD3F3] rounded-lg overflow-hidden focus:outline-none"
                  >
                    <div
                      className={`flex items-center gap-1 h-full py-1 w-max px-3 ${
                        openDropdown === filter.key
                          ? 'border-[#4F7FD1]'
                          : 'border-[#BFD3F3]'
                      }`}
                    >
                      {filter.key === 'date' ? (
                        <img src={calendarDays} alt="Date" />
                      ) : (
                        <img src={filterBlack} alt="Filter" />
                      )}
                      <span className="text-[#0D192C] text-sm">
                        {filter.label}
                      </span>
                    </div>
                    {filterValues[filter.key] && (
                      <div className="bg-[#F2F6FD] h-full py-1 px-3 flex items-center gap-1 border-l border-[#BFD3F3]">
                        <span className="text-[#0D192C] text-sm">
                          {getFilterDisplayValue(filter)}
                        </span>
                        {getCountBadge(filter) && (
                          <span className="bg-brandBlue text-white text-[8px] px-1 py-0.5 rounded">
                            {getCountBadge(filter)}
                          </span>
                        )}
                        <button
                          onClick={e => clearFilter(e, filter.key)}
                          className="focus:outline-none ml-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    {!filterValues[filter.key] && (
                      <div className="p-1 pr-3 h-full flex items-center">
                        <img src={plusBlack} alt="Plus" />
                      </div>
                    )}
                  </button>
                </div>
              ))}
            </div>

            {isOverflowing && (
              <div
                className="absolute right-[-9px] top-1/2 -translate-y-1/2"
                style={{zIndex: 10}}
              >
                <button
                  onClick={scrollRight}
                  className="bg-blue-500 border border-[#BFD3F3] bg-brandBlue rounded-full p-1 w-[18px] h-[18px] flex items-center justify-center text-white"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <button
            className="flex items-center justify-end text-[#858DAA] text-sm gap-1 py-3"
            onClick={clearAllFilters}
          >
            Clear all
            <X size={12} />
          </button>
        )}
      </div>
      <div>
        {openDropdown && currentOpenFilter && (
          <div
            ref={dropdownRef}
            className="absolute z-[99] rounded-lg mt-3 bg-white shadow-filter_dropdown ring-1 ring-black ring-opacity-5 p-2 w-[250px]"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            <div className="max-h-[450px] overflow-y-auto space-y-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {currentOpenFilter.type === 'date' &&
              !disableDefaultDateFilter ? (
                <>
                  {timeOptions.map(option => (
                    <div
                      key={option.value}
                      className={`flex items-center space-x-2 py-2 px-3 cursor-pointer rounded-[10px] transition-colors duration-150 ${
                        filterValues[openDropdown] === option.value
                          ? 'bg-[#F2F6FD]'
                          : 'hover:bg-[#F2F6FD]'
                      }`}
                      onClick={() => handleOptionSelect(openDropdown, option)}
                    >
                      <div
                        className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                          filterValues[openDropdown] === option.value
                            ? 'border-brandBlue bg-white'
                            : 'border-[#e1e1e3]'
                        }`}
                      >
                        {filterValues[openDropdown] === option.value && (
                          <div className="w-2 h-2 rounded-full bg-brandBlue"></div>
                        )}
                      </div>
                      <span
                        className={`text-sm ${
                          filterValues[openDropdown] === option.value
                            ? 'font-medium text-brandBlue'
                            : 'font-light text-body'
                        }`}
                      >
                        {option.label}
                      </span>
                    </div>
                  ))}
                  {filterValues[openDropdown] === 'specificDateRange' && (
                    <DateFilter
                      initialStartDate={startDate}
                      initialEndDate={endDate}
                      onCustomDateRangeChange={handleCustomDateRangeChange}
                    />
                  )}
                </>
              ) : currentOpenFilter.type === 'range' ? (
                <div className="p-4 space-y-4">
                  {currentOpenFilter.options.inputType === 'fields' ? (
                    <div className="flex gap-3">
                      <input
                        type="number"
                        placeholder="Min"
                        value={
                          tempRangeValues[currentOpenFilter.key]?.min ?? ''
                        }
                        onChange={e =>
                          handleTempRangeChange(
                            currentOpenFilter.key,
                            'min',
                            e.target.value,
                          )
                        }
                        min={currentOpenFilter.options.min}
                        max={currentOpenFilter.options.max}
                        step={currentOpenFilter.options.step}
                        className="w-full p-2 border border-[#BFD3F3] rounded text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={
                          tempRangeValues[currentOpenFilter.key]?.max ?? ''
                        }
                        onChange={e =>
                          handleTempRangeChange(
                            currentOpenFilter.key,
                            'max',
                            e.target.value,
                          )
                        }
                        min={currentOpenFilter.options.min}
                        max={currentOpenFilter.options.max}
                        step={currentOpenFilter.options.step}
                        className="w-full p-2 border border-[#BFD3F3] rounded text-sm"
                      />
                    </div>
                  ) : currentOpenFilter.options.inputType === 'slider' ? (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>
                          {tempRangeValues[currentOpenFilter.key]?.min ??
                            currentOpenFilter.options.min}
                        </span>
                        <span>
                          {tempRangeValues[currentOpenFilter.key]?.max ??
                            currentOpenFilter.options.max}
                        </span>
                      </div>
                      <DualRangeSlider
                        min={currentOpenFilter.options.min}
                        max={currentOpenFilter.options.max}
                        onChange={(type, value) =>
                          handleSliderChange(currentOpenFilter.key, type, value)
                        }
                      />
                    </div>
                  ) : null}
                  <button
                    className="w-full bg-brandBlue text-white py-2 rounded"
                    onClick={() => applyRangeFilter(currentOpenFilter.key)}
                  >
                    Apply
                  </button>
                </div>
              ) : (
                <>
                  {currentOpenFilter.options?.map(option => {
                    const isMultiSelect = currentOpenFilter.multiSelect;
                    const currentValues = isMultiSelect
                      ? filterValues[openDropdown] || []
                      : filterValues[openDropdown];
                    const isSelected = isMultiSelect
                      ? currentValues &&
                        Array.isArray(currentValues) &&
                        currentValues.includes(option.value)
                      : currentValues === option.value;

                    return (
                      <div
                        key={option.value}
                        className={`flex items-center space-x-2 py-2 px-3 cursor-pointer rounded-[10px] transition-colors duration-150 ${
                          isSelected ? 'bg-[#F2F6FD]' : 'hover:bg-[#F2F6FD]'
                        }`}
                        onClick={() => handleOptionSelect(openDropdown, option)}
                      >
                        {isMultiSelect ? (
                          <div
                            className={`w-4 h-4 min-w-4 min-h-4 rounded border flex items-center justify-center ${
                              isSelected
                                ? 'border-brandBlue bg-brandBlue'
                                : 'border-[#e1e1e3]'
                            }`}
                          >
                            {isSelected && <Check color="white" />}
                          </div>
                        ) : (
                          <div
                            className={`w-4 h-4 min-w-4 min-h-4 rounded-full border flex items-center justify-center ${
                              isSelected
                                ? 'border-brandBlue bg-white'
                                : 'border-[#e1e1e3]'
                            }`}
                          >
                            {isSelected && (
                              <div className="w-2 h-2 rounded-full bg-brandBlue"></div>
                            )}
                          </div>
                        )}
                        <span
                          className={`text-sm ${
                            isSelected
                              ? 'font-medium text-brandBlue'
                              : 'font-light text-body'
                          }`}
                        >
                          {option.label}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default FilterV2;
